import React, { useEffect } from 'react';
import styles from './styles.module.scss';
import moment from 'moment';
import GlobalConfig from '../../../../../config';

// manifests
import EmployeeLeaveTypes from '../../../../../manifests/types/employeeLeave';
import EmployeeLeaveStatus from '../../../../../manifests/status/employeeLeave';

// assets
import { faArrowRight, faPaperclip, faXmarkCircle } from '@fortawesome/pro-light-svg-icons';

// services
import ErrorService from '../../../../../services/general/error';
import EmployeeLeaveService from '../../../../../services/employees/leave';

// props
import { IPaginationResourceShort } from '../../../../../props/general';
import { IEmployeeLeaveResourceShortProps } from '../../../../../props/employees/leaves/leave';
import { FilterOptionProps } from '../../../../uiframeworks/filters/panel';

// components
import { ActionButton, CommandBar, Dropdown, IColumn, ICommandBarItemProps, IDropdownOption, Link, PrimaryButton, SearchBox, SelectionMode, ShimmeredDetailsList, Stack } from '@fluentui/react';
import { useStore } from '../../../../../stores/root';
import Text from './../../../../typography/text';
import SelectedFilter from '../../../../uiframeworks/filters/selected';
import Pagination from '../../../../uiframeworks/pagination';
import Tag from '../../../../uiframeworks/tag';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PivotButton from '../../../../uiframeworks/buttons/pivot';
import EmployeeLeaveForm from '../../form';
import PermissionsService from '../../../../../services/permissions';
import ConfirmEmployeeLeaveForm from '../../form/confirm';
import ApproveEmployeeLeaveForm from '../../form/approve';
import RejectEmployeeLeaveForm from '../../form/reject';
import EmployeeLeaveBalanceCards from '../../balance';

type EmployeeLeavesListProps = {
    qs?: string[];
    leaves?: IEmployeeLeaveResourceShortProps[];
    columns?: TEmployeeLeaveColumn[];
    employeeId?: string;
    hideBalance?: boolean;
    hideSearch?: boolean;
    hideCommandBar?: boolean;
    hideCreateLeave?: boolean;
    hideQuickFilter?: boolean;
    hideFilter?: boolean;
    variant?: 'card' | 'plain';
    onRefresh?: () => void;
}

export type TEmployeeLeaveColumn = 'employee' | 'actions' | 'type' | 'status' | 'date' | 'reason';

const EmployeeLeavesList: React.FC<EmployeeLeavesListProps> = (props: EmployeeLeavesListProps) => {
    const shownColumns: TEmployeeLeaveColumn[] = props.columns || [
        'employee',
        'type',
        'status',
        'date',
        'reason',
        'actions'
    ];

    const { banner, user } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [showingMore, setShowingMore] = React.useState<boolean>(false);
    const [leaves, setLeaves] = React.useState<IEmployeeLeaveResourceShortProps[]>(props.leaves || []);
    const [activeLeave, setActiveLeave] = React.useState<IEmployeeLeaveResourceShortProps | undefined>();
    const [pagination, setPagination] = React.useState<IPaginationResourceShort | undefined>();
    const [selectedFilter, setSelectedFilter] = React.useState<FilterOptionProps[]>([]);
    const [selectedStatus, setSelectedStatus] = React.useState<string>('all');
    const [selectedYear, setSelectedYear] = React.useState<string>('all');
    const [selectedType, setSelectedType] = React.useState<string>('all');
    const [keyword, setKeyword] = React.useState<string>("");
    const [activeSurface, setActiveSurface] = React.useState<string | undefined>();
    const [balanceRefreshKey, setBalanceRefreshKey] = React.useState<number>(0);

    const targetEmployeeId = React.useMemo(() => {
        if (props.employeeId) return props.employeeId;
        if (!props.qs) return undefined;
        for (const q of props.qs) {
            const match = q.match(/employee_id=([^&]+)/);
            if (match) return match[1];
        }
        return undefined;
    }, [props.qs, props.employeeId]);

    const columns: IColumn[] = [
        {
            key: "employee",
            name: "Employee Details",
            fieldName: "id",
            minWidth: 250,
            maxWidth: 250,
            onRender: (item: IEmployeeLeaveResourceShortProps) => {
                return <Stack styles={{ root: { padding: '2px 0px' } }}>
                    <Link to={"#"}><Text size={'small'}>{item.employee.name}</Text></Link>
                    <Text size={'small'}>({item.subsidiary.name})</Text>
                </Stack>
            }
        },
        {
            key: "actions",
            name: "",
            minWidth: 30,
            maxWidth: 30,
            onRender: (item: IEmployeeLeaveResourceShortProps) => {
                const menuItems = [];

                if (item.status === 'requested' && item.employee.id === user.data?.id) {
                    menuItems.push({
                        key: "update", text: "Update", iconProps: { iconName: "Edit" }, onClick: () => {
                            setActiveLeave(item);
                            setActiveSurface('leave.update');
                        }
                    });
                }

                if (item.status === 'requested' && PermissionsService.hasPermission(['employees.leaves.confirm.all'], user.permissions)) {
                    menuItems.push({
                        key: "confirm", text: "Confirm", iconProps: { iconName: "ShieldCheck" }, onClick: () => {
                            setActiveLeave(item);
                            setActiveSurface('leave.confirm');
                        }
                    });
                }

                if ((item.status === 'requested' || item.status === 'confirmed') && PermissionsService.hasPermission(['employees.leaves.approve.all'], user.permissions)) {
                    menuItems.push({
                        key: "approve", text: "Approve", iconProps: { iconName: "Check" }, onClick: () => {
                            setActiveLeave(item);
                            setActiveSurface('leave.approve');
                        }
                    });

                    menuItems.push({
                        key: "reject", text: "Reject", iconProps: { iconName: "Xmark" }, onClick: () => {
                            setActiveLeave(item);
                            setActiveSurface('leave.reject');
                        }
                    });
                }

                return <Stack.Item styles={{ root: { padding: '0px' } }}>
                    {
                        menuItems.length > 0 ? <Stack.Item className={"detailsListActionRow"}>
                            <ActionButton className={'detailsListActionButton'} menuProps={{
                                items: menuItems
                            }} />
                        </Stack.Item> : null
                    }
                </Stack.Item >;
            }
        },
        {
            key: "type",
            name: "Type",
            minWidth: 100,
            maxWidth: 100,
            onRender: (item: IEmployeeLeaveResourceShortProps) => {
                const type = EmployeeLeaveTypes.find((t) => item.type === t.key);

                return <Stack styles={{ root: { padding: '2px 0px' } }}>
                    <Text size={'small'}>{type?.text || "-"}</Text>
                </Stack>
            }
        },
        {
            key: "date",
            name: "Date",
            minWidth: 200,
            maxWidth: 220,
            onRender: (item: IEmployeeLeaveResourceShortProps) => {
                return <Stack styles={{ root: { padding: '2px 0px' } }} tokens={{ childrenGap: 2 }}>
                    <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                        <Text size={'small'}>{moment(item.startDate).format("DD/MM/YYYY")}</Text>
                        <Text size={'small'}><FontAwesomeIcon icon={faArrowRight} /></Text>
                        <Text size={'small'}>{moment(item.endDate).format("DD/MM/YYYY")}</Text>
                    </Stack>
                    {item.totalDays ? <Text size={'xsmall'} style={{ color: '#888' }}>({item.totalDays} days)</Text> : null}
                </Stack>
            }
        },
        {
            key: "reason",
            name: "Reason",
            minWidth: 200,
            isMultiline: true,
            onRender: (item: IEmployeeLeaveResourceShortProps) => {
                return <Stack styles={{ root: { padding: '2px 0px' } }} tokens={{ childrenGap: 4 }}>
                    <Text size={'small'}>{item.reason}</Text>
                    {item.attachmentUrl && (
                        <div>
                            {item.attachmentUrl.startsWith('http') ? (
                                <Link href={item.attachmentUrl} target="_blank" rel="noreferrer" style={{ fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                    <FontAwesomeIcon icon={faPaperclip} /> Lihat Lampiran / SKD
                                </Link>
                            ) : (
                                <span style={{ fontSize: 11, color: '#0284c7', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                    <FontAwesomeIcon icon={faPaperclip} /> Ada Lampiran Dokumen
                                </span>
                            )}
                        </div>
                    )}
                </Stack>
            }
        },
        {
            key: "status",
            name: "Status",
            minWidth: 100,
            maxWidth: 100,
            onRender: (item: IEmployeeLeaveResourceShortProps) => {
                const status = EmployeeLeaveStatus.find((s) => item.status === s.key);

                return <Stack horizontal styles={{ root: { padding: '0px' } }}>
                    {status ? <Tag size={'small'} variant={status.tagVariant} text={status.text} icon={status.icon} /> : null}
                </Stack>
            }
        },
    ];

    useEffect(() => {
        if (!props.leaves) {
            _onRetrieveLeaves();
        } else {
            setLoaded(true);
        }
    }, [keyword, selectedStatus, selectedYear, selectedType, window.location.search]);

    const getQueryString = () => {
        let qs: string[] = JSON.parse(JSON.stringify(props.qs || []));
        const params = (new URLSearchParams(window.location.search));

        // get existing query string
        const _p = params.get("_p");

        qs.push(`top=${GlobalConfig.defaultTop}`);
        if (selectedStatus && selectedStatus !== 'all') {
            qs.push(`status=${selectedStatus}`);
        }
        if (selectedYear && selectedYear !== 'all') {
            qs.push(`year=${selectedYear}`);
        }
        if (selectedType && selectedType !== 'all') {
            qs.push(`type=${selectedType}`);
        }
        if ((_p || "").trim() !== "") { qs.push(`page=${_p}`) }
        if (keyword && keyword.trim() !== "") { qs.push(`search=${keyword}`) }

        qs = qs.filter(function (item, pos) {
            return qs.indexOf(item) == pos;
        });

        return qs.join("&");
    }

    const _onRetrieveLeaves = async () => {
        try {
            setLoaded(false);
            const qs = getQueryString();
            const results = await EmployeeLeaveService.retrieve(qs);

            setLeaves(results.data);
            setPagination(results.pagination);
            setLoaded(true);
        } catch (e) {
            banner.add({
                key: 'retrieve_leaves_list_error',
                text: 'Failed to retrieve employee leaves list. Error: ' + ErrorService.getMessage(e),
                icon: faXmarkCircle,
                variant: 'error'
            });
        }
    }

    const _onKeywordChanged = (value?: string) => {
        setLoaded(true);
        setKeyword(value || "");
    }

    const currentYear = moment().year();
    const yearOptions: IDropdownOption[] = [
        { key: 'all', text: 'All Years' }
    ];
    for (let y = currentYear + 1; y >= currentYear - 5; y--) {
        yearOptions.push({ key: y.toString(), text: `${y}` });
    }

    const typeOptions: IDropdownOption[] = [
        { key: 'all', text: 'All Leave Types' },
        ...EmployeeLeaveTypes.map((t) => ({ key: t.key, text: t.text }))
    ];

    const getCommandBarItems = () => {
        let items: ICommandBarItemProps[] = [];
        let farItems: ICommandBarItemProps[] = [];

        if (!props.hideSearch) {
            items.push({
                key: "search",
                onRender: () => {
                    return <SearchBox
                        placeholder={"Search ..."}
                        value={keyword}
                        onChange={(e, val) => {
                            if (!val) {
                                _onKeywordChanged("");
                            }
                        }}
                        onSearch={_onKeywordChanged}
                        onClear={() => _onKeywordChanged("")}
                        styles={{ root: { width: 200, marginRight: 10 } }}
                    />
                }
            });
        }

        items.push({
            key: "filter_year",
            onRender: () => {
                return <Dropdown
                    placeholder="Select Year"
                    options={yearOptions}
                    selectedKey={selectedYear}
                    styles={{
                        root: { width: 140, marginRight: 10 }
                    }}
                    onChange={(evt, opt) => {
                        setSelectedYear(opt ? opt.key as string : 'all');
                    }}
                />;
            }
        });

        items.push({
            key: "filter_type",
            onRender: () => {
                return <Dropdown
                    placeholder="All Leave Types"
                    options={typeOptions}
                    selectedKey={selectedType}
                    styles={{
                        root: { width: 160, marginRight: 10 }
                    }}
                    onChange={(evt, opt) => {
                        setSelectedType(opt ? opt.key as string : 'all');
                    }}
                />;
            }
        });

        const isFiltered = selectedYear !== 'all' || selectedType !== 'all' || selectedStatus !== 'all' || (keyword || '').trim() !== '';
        if (isFiltered) {
            items.push({
                key: "reset_filter",
                onRender: () => {
                    return <ActionButton
                        iconProps={{ iconName: "ClearFilter" }}
                        text="Reset Filter"
                        onClick={() => {
                            setSelectedYear('all');
                            setSelectedType('all');
                            setSelectedStatus('all');
                            setKeyword('');
                        }}
                    />;
                }
            });
        }

        if (!props.hideCreateLeave) {
            farItems.push({
                key: "register",
                text: "Request leave",
                iconProps: { iconName: "Add" },
                onRender: () => {
                    return <PrimaryButton text={"Request leave"}
                        iconProps={{ iconName: "Add" }}
                        styles={{ root: { marginLeft: 10 } }}
                        onClick={() => setActiveSurface('leave.request')} />;
                }
            });
        }

        return { items, farItems };
    }

    const _onSurfaceDismissed = (refresh?: boolean, status?: string) => {
        setActiveSurface(undefined);
        setActiveLeave(undefined);

        if (status) { 
            setSelectedStatus(status);
        } else if (refresh) { 
            _onRetrieveLeaves();
        }
        if (refresh || status) {
            setBalanceRefreshKey(prev => prev + 1);
            props.onRefresh?.();
        }
    }

    return <Stack className={styles.container} tokens={{ childrenGap: 20 }}>
        {!props.hideCommandBar ? <CommandBar
            items={getCommandBarItems().items}
            farItems={getCommandBarItems().farItems}
            styles={{
                root: {
                    padding: 0,
                    height: 'unset',
                    backgroundColor: 'transparent'
                }
            }}
            ariaLabel="Use left and right arrow keys to navigate between commands" /> : null}
        {selectedFilter.length > 0 ? <SelectedFilter selected={selectedFilter} onChange={(selected) => {
            setSelectedFilter(selected);
        }} /> : null}
        {targetEmployeeId && !props.hideBalance ? (
            <EmployeeLeaveBalanceCards
                employeeId={targetEmployeeId}
                year={selectedYear}
                refreshTrigger={balanceRefreshKey}
            />
        ) : null}
        <Stack tokens={{ childrenGap: 10 }}>
            <Stack className={styles[props.variant || 'card']} tokens={{ childrenGap: 20 }}>
                {pagination && leaves.length > 0 ? <Pagination currentPage={pagination.currentPage} totalPage={pagination.lastPage} text={pagination.total + " leaves found"} /> : null}
                {props.hideQuickFilter ? null : <Stack tokens={{ childrenGap: 20 }} horizontal wrap>
                    {EmployeeLeaveStatus.length > 0 ? <PivotButton buttons={[
                        {
                            key: 'all',
                            text: 'All Status',
                            active: selectedStatus === 'all',
                            onClick: () => {
                                setSelectedStatus('all');
                            }
                        },
                        ...EmployeeLeaveStatus.map((status) => {
                            return {
                                key: status.key,
                                text: status.text,
                                active: selectedStatus === status.key,
                                onClick: () => {
                                    setSelectedStatus(status.key);
                                }
                            }
                        })
                    ]} /> : null}
                </Stack>}
                {
                    !loaded || (loaded && leaves.length > 0) ? (
                        <>
                            <ShimmeredDetailsList
                                setKey="items"
                                items={leaves}
                                columns={columns.filter((col) => shownColumns.indexOf(col.key as TEmployeeLeaveColumn) > -1)}
                                selectionMode={SelectionMode.none}
                                enableShimmer={!loaded}
                                onShouldVirtualize={() => false}
                                ariaLabelForShimmer="Content is being fetched"
                                ariaLabelForGrid="Item details" />
                        </>
                    ) : null
                }
                {loaded && leaves.length < 1 ? <Stack styles={{ root: { padding: 10 } }}>
                    <Text>Leaves not found</Text>
                </Stack> : null}
            </Stack>
            {pagination && leaves.length > 0 ? <Pagination currentPage={pagination.currentPage} totalPage={pagination.lastPage} /> : null}
        </Stack>
        {activeSurface === 'leave.request' ? <EmployeeLeaveForm defaultEmployeeId={targetEmployeeId} onDismissed={_onSurfaceDismissed} /> : null}
        {activeSurface === 'leave.update' && activeLeave ? <EmployeeLeaveForm leaveId={activeLeave.id} onDismissed={_onSurfaceDismissed} /> : null}
        {activeSurface === 'leave.confirm' && activeLeave ? <ConfirmEmployeeLeaveForm leaveId={activeLeave.id} onDismissed={_onSurfaceDismissed} /> : null}
        {activeSurface === 'leave.approve' && activeLeave ? <ApproveEmployeeLeaveForm leaveId={activeLeave.id} onDismissed={_onSurfaceDismissed} /> : null}
        {activeSurface === 'leave.reject' && activeLeave ? <RejectEmployeeLeaveForm leaveId={activeLeave.id} onDismissed={_onSurfaceDismissed} /> : null}
    </Stack>;
};

export default EmployeeLeavesList;
